import { useCallback, useEffect, useRef, useState } from "react";
import { client } from "@/utils/uno-axios";
import { calcUrl } from "@/features/ui/helpers/fetcher-helpers";

// 이 브라우저에서 안내를 이미 띄웠는지. 서버 기록이 진짜 기준이고, 이 값은 같은 브라우저에서
// 다시 묻지 않기 위한 것이다(GET 이 실패해도 매번 다시 묻지 않게).
const ORDER_SHEET_VIEW_TIP_SEEN_KEY = "orderSheetViewTipSeen";

export const orderSheetViewTipTemplateUrl =
  "/buyers/:buyerId/order-sheet-view-tip";

interface UseOrderSheetViewTipParameter {
  // 바이어가 아니면 undefined — 띄우지 않는다
  buyerId: number | undefined;
  // 주문서를 불러온 뒤에만 띄운다(버튼이 화면에 있을 때)
  isEnabled: boolean;
  // 화면을 열 때 카드 보기였는지. 기본은 자세히 보기라, 카드 보기로 열렸다면 이미 바꿔 본 사람이다.
  isGalleryViewOnOpen: boolean;
}

function readSeen() {
  try {
    return localStorage.getItem(ORDER_SHEET_VIEW_TIP_SEEN_KEY) === "true";
  } catch {
    return false;
  }
}

function writeSeen() {
  try {
    localStorage.setItem(ORDER_SHEET_VIEW_TIP_SEEN_KEY, "true");
  } catch {
    // 저장소를 못 쓰는 브라우저 — 서버 기록으로 충분하다
  }
}

/**
 * 카드 보기 / 자세히 보기 전환 안내 — 보기를 한 번도 바꿔 본 적 없는 사용자에게 최초 1회만 띄운다.
 *
 * 판정은 서버(user_action_log 의 보기 전환·안내 표시 기록, 사용자 기준)가 하고, 띄우는 순간
 * 서버에 '띄웠다'고 남겨 다른 기기에서도 다시 뜨지 않게 한다. 확인을 누르지 않고 넘어가도 1회로 친다.
 * 서버의 보기 기록은 2026-10-14 부터라, 그 전에 카드 보기로 바꿔 둔 사람은 브라우저 저장값으로 거른다.
 */
export default function useOrderSheetViewTip({
  buyerId,
  isEnabled,
  isGalleryViewOnOpen,
}: UseOrderSheetViewTipParameter) {
  const [isOpen, setIsOpen] = useState(false);
  // 화면을 연 동안 한 번만 묻는다(재검증·개발 모드 effect 재실행으로 겹치지 않게)
  const checkedRef = useRef(false);
  const galleryOnOpenRef = useRef(isGalleryViewOnOpen);

  useEffect(() => {
    if (!isEnabled || buyerId === undefined || checkedRef.current) {
      return;
    }
    checkedRef.current = true;
    if (readSeen()) {
      return;
    }
    if (galleryOnOpenRef.current) {
      writeSeen();
      return;
    }
    const url = calcUrl(orderSheetViewTipTemplateUrl, { buyerId });
    client
      .get<{ show: boolean }>(url)
      .then(({ data }) => {
        if (!data?.show) {
          writeSeen();
          return;
        }
        setIsOpen(true);
        writeSeen();
        client.post(url).catch(() => {
          // 기록 실패는 무시한다 — 이 브라우저에서는 이미 다시 안 뜬다
        });
      })
      .catch(() => {
        // 조회 실패 시 띄우지 않는다(다음에 다시 묻는다)
        checkedRef.current = false;
      });
  }, [isEnabled, buyerId]);

  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, close };
}
